import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import StatusModal from '@/components/ui/StatusModal';
import MessageModal from '@/components/ui/MessageModal';
import LoadingButton from '@/components/ui/LoadingButton';
import IdolSelector from '@/components/ui/IdolSelector';
import Axios from '@/shared/api/axios';

const EMPTY_EVENT_TYPES = {
  newMessage: false,
  messageRead: false,
  newSubscription: false,
  tipReceived: false,
  subscriptionExpiry: false
};

function WebhookSettings() {
  const { t } = useTranslation();
  const statusModalRef = useRef();
  const successModalRef = useRef();
  const resetConfirmModalRef = useRef();
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const [selectedIdol, setSelectedIdol] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [favluvToken, setFavluvToken] = useState('');
  const [gptToken, setGptToken] = useState('');
  const [hasFavluvToken, setHasFavluvToken] = useState(false);
  const [hasGptToken, setHasGptToken] = useState(false);
  const [isEditingFavluvToken, setIsEditingFavluvToken] = useState(false);
  const [isEditingGptToken, setIsEditingGptToken] = useState(false);
  const [eventTypes, setEventTypes] = useState(EMPTY_EVENT_TYPES);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('idle');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // 表單目前載入的是哪個偶像的設定。切換偶像後尚未載入完成、或載入失敗時會與 selectedIdol 不一致，
  // 此時表單仍是前一個偶像的值，停用儲存、測試連線、重置，避免套用到新選的偶像
  const [loadedIdolId, setLoadedIdolId] = useState(null);
  const isFormReady = loadedIdolId === selectedIdol;
  // 防亂序：快速切換偶像時，只套用最後一次請求的結果
  const fetchSeqRef = useRef(0);

  // 套用「尚未設定 webhook」的表單狀態（偶像沒有設定資料、或重置成功後）
  const applyEmptySettings = () => {
    setWebhookUrl('');
    setFavluvToken('');
    setGptToken('');
    setHasFavluvToken(false);
    setHasGptToken(false);
    setIsEditingFavluvToken(false);
    setIsEditingGptToken(false);
    setEventTypes(EMPTY_EVENT_TYPES);
    setConnectionStatus('idle');
    setOriginalData({
      webhookUrl: '',
      favluvToken: '',
      gptToken: '',
      eventTypes: EMPTY_EVENT_TYPES
    });
    setHasChanges(false);
  };

  // 取得 webhook 設定
  const fetchWebhookSettings = async (idolId) => {
    if (!idolId) return;

    const seq = ++fetchSeqRef.current;
    try {
      const response = await Axios.get('/dev/webhook', {
        params: { idol_id: idolId }
      });
      if (seq !== fetchSeqRef.current) return;

      const statusCode = response.data?.status_code;
      if (statusCode !== 'SYSTEM_1000') {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
        return;
      }

      const data = response.data.data;
      if (!data) {
        // 尚未設定過 webhook：顯示空白表單，不沿用前一個偶像留在表單上的值
        applyEmptySettings();
        setLoadedIdolId(idolId);
        return;
      }

      // 直接設定 webhook_url，null 就是空值
      setWebhookUrl(data.webhook_url || '');

      const newEventTypes = {
        newMessage: data.event_new_message || false,
        messageRead: data.event_message_read || false,
        newSubscription: data.event_new_subscription || false,
        tipReceived: data.event_tip_received || false,
        subscriptionExpiry: data.event_subscription_expired || false
      };

      setEventTypes(newEventTypes);

      // token 狀態：後端若回傳 "********" 代表已有設定，null 代表尚未設定
      const hasFav = data.favluv_dev_token === '********';
      const hasGpt = data.gpt_token === '********';

      setHasFavluvToken(hasFav);
      setHasGptToken(hasGpt);
      setFavluvToken('');
      setGptToken('');
      setIsEditingFavluvToken(false);
      setIsEditingGptToken(false);

      // 儲存原始資料（前端永遠不記實際 token 值）
      setOriginalData({
        webhookUrl: data.webhook_url || '',
        favluvToken: '',
        gptToken: '',
        eventTypes: newEventTypes
      });

      setHasChanges(false);

      // 如果 webhook_url 不為空，設定為 success 狀態
      if (data.webhook_url) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('idle');
      }
      setLoadedIdolId(idolId);
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to fetch webhook settings:', error);
      }
    }
  };

  // 保存 webhook 設定
  const saveWebhookSettings = async () => {
    // 儲存、測試、重置的按鈕已在表單不是目前偶像的設定時停用，這裡再擋一次
    if (!selectedIdol || !isFormReady) return;
    // 儲存、測試、重置途中若切換偶像，effect 已送出新偶像的讀取（序號前進）；回來後不可再動表單，
    // 也不可重抓舊偶像（舊偶像的重抓會作廢新偶像的讀取，表單停在舊偶像、按鈕一直停用）
    const seqAtSave = fetchSeqRef.current;

    try {
      const payload = {
        idol_id: selectedIdol,
        webhook_url: webhookUrl,
        event_new_message: eventTypes.newMessage,
        event_message_read: eventTypes.messageRead,
        event_new_subscription: eventTypes.newSubscription,
        event_tip_received: eventTypes.tipReceived,
        event_subscription_expired: eventTypes.subscriptionExpiry
      };

      // Favluv Dev Token：只有在有輸入新 token 時才覆寫
      if (favluvToken) {
        payload.favluv_dev_token = favluvToken;
      }

      // OpenAI Platform API key：只有在有輸入新 token 時才覆寫
      if (gptToken) {
        payload.gpt_token = gptToken;
      }

      const response = await Axios.post('/dev/save-webhook', payload);

      const statusCode = response.data?.status_code;
      if (statusCode === 'SYSTEM_1000') {
        // 成功時顯示成功訊息
        if (seqAtSave === fetchSeqRef.current) {
          await fetchWebhookSettings(selectedIdol);
        }
        successModalRef.current?.show();
      } else if (statusCode) {
        // 其他狀態碼顯示錯誤
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to save webhook:', error);
      }
    }
  };

  // 測試連接
  const testWebhookConnection = async () => {
    if (!selectedIdol || !isFormReady || !webhookUrl) return;
    const seqAtTest = fetchSeqRef.current;

    setIsTesting(true);
    setConnectionStatus('idle');
    try {
      const response = await Axios.post('/dev/test-webhook', {
        idol_id: selectedIdol,
        webhook_url: webhookUrl
      });
      // 測試途中已切換偶像：結果屬於前一個偶像，不寫進目前偶像的連線狀態
      if (seqAtTest !== fetchSeqRef.current) return;

      // 檢查是否連結成功
      if (response.data && response.data.status_code === 'SYSTEM_1000' && response.data.remote_status === 200) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('failed');
      }
    } catch (error) {
      console.error('Failed to test webhook:', error);
      if (seqAtTest !== fetchSeqRef.current) return;
      setConnectionStatus('failed');
    } finally {
      setIsTesting(false);
    }
  };

  // 重置 webhook 設定
  const resetWebhookSettings = async () => {
    if (!selectedIdol || !isFormReady) return;
    const seqAtReset = fetchSeqRef.current;

    try {
      const response = await Axios.post('/dev/save-webhook', {
        idol_id: selectedIdol,
        webhook_url: '',
        favluv_dev_token: '',
        gpt_token: '',
        event_new_message: false,
        event_message_read: false,
        event_new_subscription: false,
        event_tip_received: false,
        event_subscription_expired: false
      });

      const statusCode = response.data?.status_code;
      if (statusCode === 'SYSTEM_1000') {
        // 成功時清空所有欄位（重置途中已切換偶像時，表單是新偶像的設定，不可清空）
        if (seqAtReset === fetchSeqRef.current) {
          applyEmptySettings();
        }
        successModalRef.current?.show();
      } else if (statusCode) {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to reset webhook:', error);
      }
    }
  };

  // 顯示重置確認 modal
  const handleResetClick = () => {
    resetConfirmModalRef.current?.show();
  };


  // 當選中偶像改變時載入設定
  useEffect(() => {
    if (selectedIdol) {
      fetchWebhookSettings(selectedIdol);
    }
    // 只在 selectedIdol 改變時載入；fetchWebhookSettings 每次 render 都是新函式，列入依賴會每次 render 都重抓
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdol]);

  const handleEventTypeChange = (eventType) => {
    setEventTypes(prev => ({
      ...prev,
      [eventType]: !prev[eventType]
    }));
    setHasChanges(true);
  };

  // 檢查是否有變更
  useEffect(() => {
    if (!originalData) return;

    const hasChanged =
      webhookUrl !== originalData.webhookUrl ||
      favluvToken !== originalData.favluvToken ||
      gptToken !== originalData.gptToken ||
      JSON.stringify(eventTypes) !== JSON.stringify(originalData.eventTypes);

    setHasChanges(hasChanged);
  }, [webhookUrl, favluvToken, gptToken, eventTypes, originalData]);

  // 當 webhook URL 變更時，重置連接狀態
  useEffect(() => {
    if (!originalData) return;

    if (webhookUrl !== originalData.webhookUrl) {
      setConnectionStatus('idle');
    }
  }, [webhookUrl, originalData]);

  return (
    <div>
      <h3 className="mb-4">{t('webhook_settings')}</h3>
      <IdolSelector
        value={selectedIdol}
        onChange={setSelectedIdol}
        className="mb-4"
      />

      <div className="bg-white rounded p-4 mb-4">
        {/* Webhook URL 區塊 - 獨立一行 */}
        <div className="mb-4">
          <label className="form-label">{t('webhook_url')}</label>
          <div className="d-flex gap-4">
            <input
              type="url"
              className="form-control"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://"
            />
            <LoadingButton
              className="btn btn-primaryF rounded-pill btn-sm d-inline-flex align-items-center text-nowrap"
              disabled={!isFormReady || !webhookUrl?.startsWith('https://') || connectionStatus === 'success'}
              onClick={testWebhookConnection}
              isLoading={isTesting}
            >
              {isTesting ? t('connecting') :
                connectionStatus === 'success' ? (
                  <>
                    <FontAwesomeIcon icon={["fas", "circle-check"]} className="me-2" size="lg" />
                    {t('connected_successfully')}
                  </>
                ) :
                  connectionStatus === 'failed' ? (
                    <>
                      <FontAwesomeIcon icon={["fas", "circle-xmark"]} className="me-2" size="lg" />
                      {t('connection_failed')}
                    </>
                  ) :
                    t('test_connection')}
            </LoadingButton>
          </div>
          <small className="form-text text-muted d-block">{t('webhook_url_note')}</small>
        </div>

        {/* 只有在連接成功時才顯示下方內容 */}
        {connectionStatus === 'success' && (
          <>
            <div className="row g-5">
              <div className="col-12 col-md-7">
                <div>
                  <div className="mb-4">
                    <label className="form-label">Favluv Dev Token</label>
                    <input
                      type="password"
                      className="form-control"
                      value={hasFavluvToken && !isEditingFavluvToken ? 'hasToken' : favluvToken}
                      onFocus={() => {
                        if (hasFavluvToken && !isEditingFavluvToken) {
                          setIsEditingFavluvToken(true);
                          setFavluvToken('');
                        }
                      }}
                      onChange={(e) => {
                        setFavluvToken(e.target.value);
                      }}
                      onBlur={() => {
                        // 點進 input 但沒有輸入任何字，移出焦點後恢復原本顯示
                        if (hasFavluvToken && isEditingFavluvToken && !favluvToken) {
                          setIsEditingFavluvToken(false);
                        }
                      }}
                      placeholder="Favluv Dev Token"
                    />
                  </div>

                  <div>
                    <label className="form-label">OpenAI Platform API key</label>
                    <input
                      type="password"
                      className="form-control"
                      value={hasGptToken && !isEditingGptToken ? 'hasToken' : gptToken}
                      onFocus={() => {
                        if (hasGptToken && !isEditingGptToken) {
                          setIsEditingGptToken(true);
                          setGptToken('');
                        }
                      }}
                      onChange={(e) => {
                        setGptToken(e.target.value);
                      }}
                      onBlur={() => {
                        if (hasGptToken && isEditingGptToken && !gptToken) {
                          setIsEditingGptToken(false);
                        }
                      }}
                      placeholder="OpenAI Platform API key"
                    />
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-5">
                <div>
                  <h6 className="fs-18 mb-3">{t('event_types')}</h6>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eventTypes.newMessage}
                      onChange={() => handleEventTypeChange('newMessage')}
                      id="newMessage"
                    />
                    <label className="form-check-label" htmlFor="newMessage">
                      {t('new_message')}
                    </label>
                  </div>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eventTypes.messageRead}
                      onChange={() => handleEventTypeChange('messageRead')}
                      id="messageRead"
                    />
                    <label className="form-check-label" htmlFor="messageRead">
                      {t('message_read')}
                    </label>
                  </div>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eventTypes.newSubscription}
                      onChange={() => handleEventTypeChange('newSubscription')}
                      id="newSubscription"
                    />
                    <label className="form-check-label" htmlFor="newSubscription">
                      {t('new_subscription')}
                    </label>
                  </div>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eventTypes.tipReceived}
                      onChange={() => handleEventTypeChange('tipReceived')}
                      id="tipReceived"
                    />
                    <label className="form-check-label" htmlFor="tipReceived">
                      {t('tip_received')}
                    </label>
                  </div>
                  <div className="form-check mb-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={eventTypes.subscriptionExpiry}
                      onChange={() => handleEventTypeChange('subscriptionExpiry')}
                      id="subscriptionExpiry"
                    />
                    <label className="form-check-label" htmlFor="subscriptionExpiry">
                      {t('subscription_expiry')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-3 mt-4">
              <button
                className="btn btn-outline-primaryF rounded-pill"
                onClick={handleResetClick}
                disabled={!isFormReady}
              >
                {t('reset')}
              </button>
              <button
                className="btn btn-primaryF rounded-pill"
                onClick={saveWebhookSettings}
                disabled={!hasChanges || !isFormReady}
              >
                {t('save')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Success Modal */}
      <MessageModal ref={successModalRef} status="success" size="md">
        <h5 className="mb-5">{t('webhook_saved_successfully')}</h5>
      </MessageModal>

      {/* Reset Confirm Modal */}
      <MessageModal ref={resetConfirmModalRef} status="warning" size="md">
        <h5 className="mb-4">{t('confirm_reset')}</h5>
        <p className="mb-5">{t('reset_warning_message')}</p>
        <div className="d-flex justify-content-end gap-3">
          <button
            className="btn btn-neutral rounded-pill"
            onClick={() => resetConfirmModalRef.current?.hide()}
          >
            {t('cancel')}
          </button>
          <button
            className="btn btn-primary rounded-pill"
            onClick={() => {
              resetConfirmModalRef.current?.hide();
              resetWebhookSettings();
            }}
          >
            {t('confirm')}
          </button>
        </div>
      </MessageModal>

      {/* Status Modal */}
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </div>
  );
}

export default WebhookSettings;